// hooks/useRegionalEnrollmentData.js
import { useMemo } from "react";

/**
 * Hook for processing enrollment data for regional/district overview
 * Aggregates enrollment by school/district for a given cluster or program area
 *
 * @param {Object} params
 * @param {Array} params.cipEnrolled - Array of CIP enrollment data (renton from context)
 * @param {Array} params.listSchools - Array of school/district names
 * @param {string} params.filterSession - Current filter (cluster name, program area, or nontrad)
 * @param {Object} params.nontrad - Object with female/male arrays of nontrad CIP codes
 * @param {number} params.yearIndex - Index for year selection (0-5, default 4 for most recent)
 * @param {string} params.districtName - Name of the district/ESD
 */
export function useRegionalEnrollmentData({
  cipEnrolled,
  listSchools,
  filterSession,
  nontrad,
  yearIndex = 4,
  districtName
}) {
  const hasData = !!(cipEnrolled?.length && listSchools?.length);

  // Helper to check if a CIP matches the current filter
  const matchesFilter = (item) => {
    if (filterSession === "nontrad-female") {
      return nontrad?.female?.includes(item.cipcode);
    } else if (filterSession === "nontrad-male") {
      return nontrad?.male?.includes(item.cipcode);
    }
    return item.clusterName === filterSession || item.programArea === filterSession;
  };

  // Process data by school/district
  const schoolData = useMemo(() => {
    if (!hasData) return [];

    // Filter CIPs by current selection
    const filteredCips = cipEnrolled.filter(matchesFilter);

    // Build a map of school -> { totalEnrollment, cips: [...] }
    const schoolMap = new Map();

    // Initialize all schools with zero values
    listSchools.forEach(schoolName => {
      schoolMap.set(schoolName, {
        schoolName,
        totalEnrollment: 0,
        cips: [],
        cipCount: 0
      });
    });

    // Process each CIP (exclude "Total" aggregate row)
    filteredCips.forEach(cip => {
      if (!cip.enrolled) return;

      cip.enrolled.forEach(schoolEnrollment => {
        const { schoolName, enrollment } = schoolEnrollment;
        if (schoolName === "Total") return; // Skip aggregate row
        if (!schoolMap.has(schoolName)) return;

        const enrollmentValue = enrollment?.[yearIndex] ?? 0;
        const school = schoolMap.get(schoolName);

        // Add to total
        school.totalEnrollment += enrollmentValue;

        // Track CIP-level data if there's enrollment
        if (enrollmentValue > 0) {
          school.cips.push({
            cipcode: cip.cipcode,
            cipcodeName: cip.cipcodeName,
            enrollment: enrollmentValue,
            // Include full enrollment array for potential multi-year view
            enrollmentHistory: enrollment
          });
          school.cipCount++;
        }
      });
    });

    // Convert to array and sort CIPs within each school by enrollment (desc)
    const result = Array.from(schoolMap.values()).map(school => ({
      ...school,
      cips: school.cips.sort((a, b) => b.enrollment - a.enrollment)
    }));

    return result;
  }, [hasData, cipEnrolled, listSchools, filterSession, nontrad, yearIndex]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    if (!schoolData.length) {
      return {
        totalEnrollment: 0,
        totalCips: 0,
        schoolCount: 0,
        avgEnrollment: 0,
        maxEnrollment: 0
      };
    }

    const totalEnrollment = schoolData.reduce((sum, s) => sum + s.totalEnrollment, 0);
    const schoolsWithEnrollment = schoolData.filter(s => s.totalEnrollment > 0);

    // Get unique CIP codes across all schools
    const uniqueCips = new Set();
    schoolData.forEach(school => {
      school.cips.forEach(cip => uniqueCips.add(cip.cipcode));
    });

    const maxEnrollment = Math.max(...schoolData.map(s => s.totalEnrollment), 1);

    return {
      totalEnrollment,
      totalCips: uniqueCips.size,
      schoolCount: schoolData.length,
      schoolsWithEnrollment: schoolsWithEnrollment.length,
      avgEnrollment: schoolsWithEnrollment.length > 0
        ? Math.round(totalEnrollment / schoolsWithEnrollment.length)
        : 0,
      maxEnrollment
    };
  }, [schoolData]);

  // Sort options - returns sorted copy based on criteria
  const getSortedData = (sortBy = 'enrollment', sortDir = 'desc') => {
    const sorted = [...schoolData];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case 'name':
          aVal = a.schoolName.toLowerCase();
          bVal = b.schoolName.toLowerCase();
          return sortDir === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'enrollment':
          aVal = a.totalEnrollment;
          bVal = b.totalEnrollment;
          break;
        case 'cipCount':
          aVal = a.cipCount;
          bVal = b.cipCount;
          break;
        default:
          aVal = a.totalEnrollment;
          bVal = b.totalEnrollment;
      }

      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  };

  // Get CIP totals across all schools (for the expanded total row)
  const cipTotals = useMemo(() => {
    if (!hasData) return [];

    const filteredCips = cipEnrolled.filter(matchesFilter);

    const cipMap = new Map();

    filteredCips.forEach(cip => {
      if (!cip.enrolled) return;

      let totalForCip = 0;
      cip.enrolled.forEach(schoolEnrollment => {
        if (schoolEnrollment.schoolName === "Total") return; // Skip aggregate row
        const enrollmentValue = schoolEnrollment.enrollment?.[yearIndex] ?? 0;
        totalForCip += enrollmentValue;
      });

      if (totalForCip > 0) {
        cipMap.set(cip.cipcode, {
          cipcode: cip.cipcode,
          cipcodeName: cip.cipcodeName,
          enrollment: totalForCip
        });
      }
    });

    return Array.from(cipMap.values()).sort((a, b) => b.enrollment - a.enrollment);
  }, [hasData, cipEnrolled, filterSession, nontrad, yearIndex]);

  return {
    schoolData,
    summary,
    cipTotals,
    getSortedData,
    hasData,
    districtName
  };
}
